import React from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Container,
  Grid,
  Typography,
  useTheme,
  alpha,
  Divider,
  Paper,
  Stack,
  Chip
} from '@mui/material';
import { 
  CheckCircleOutline as CheckIcon, 
  StarOutline as StarIcon 
} from '@mui/icons-material';
import { UserRole } from '../../types/auth';

interface SubscriptionPlansProps {
  onSelect: (role: UserRole) => void;
  currentRole?: UserRole;
  showRegisterButton?: boolean;
  containerBackgroundColor?: string;
  customButtonText?: (role: UserRole) => string;
}

export const plans = [
  {
    title: 'Free',
    price: '$0',
    period: 'forever',
    role: 'free' as UserRole,
    features: [
      'Daily basic market insights',
      'Limited dashboard access',
      'Standard technical indicators'
    ],
    color: '#4caf50' // success color
  },
  {
    title: 'Base',
    price: '$15',
    period: 'month',
    role: 'base' as UserRole,
    features: [
      'Daily Market report for Balanced Investor',
      'Performance analysis relative to historical benchmarks',
      'Market Money Flow analysis',
      'Risk management strategies',
      'Sector & MarketCap analysis',
      'Investment allocation suggestions',
    ],
    color: '#2196f3' // primary/info color
  },
  {
    title: 'Premium',
    price: '$20',
    originalPrice: '$49',
    period: 'month',
    role: 'premium' as UserRole,
    highlighted: true,
    features: [
      'Everything in Base',
      'Unlock all investment profiles',
      'Daily market ratings for all investment profiles',
      'Detailed AI Ratings for 2600+ Stocks',
      'Custom Watchlists and Stock screeners',
      // 'Multiple "Investment Profiles"',
      // 'Daily reports and actions customized for profile',
      // '2600+ stocks',
      // 'Watchlists and screeners',
      // 'Entry and exit points', 
    ],
    color: '#ff9800' // warning/secondary color
  },
  {
    title: 'Pro',
    price: '$100',
    period: 'month',
    role: 'premium_plus' as UserRole,
    disabled: true,
    comingSoon: true,
    features: [
      'Everything in Premium',
      'AI-enabled Portfolio Health check',
      'Custom portfolio creation',
      'AI-enabled Portfolio management',
      'Buy/Sell signals within portfolios',
      'Portfolio performance tracking',
      'Dedicated support',
      // 'AI Managed Accounts',
      // 'Buy and Sell signals',
      // 'Portfolio rebalancing by Investment Profile',
      // 'Dedicated support',
      // 'Custom indicators',
    ],
    color: '#9c27b0' // purple
  },
];

export const SubscriptionPlans: React.FC<SubscriptionPlansProps> = ({
  onSelect,
  currentRole,
  showRegisterButton = false,
  containerBackgroundColor,
  customButtonText
}) => {
  const theme = useTheme();
  
  const getButtonText = (plan: typeof plans[0]) => {
    if (customButtonText) {
      return customButtonText(plan.role);
    }
    
    if (plan.comingSoon) return 'Coming Soon';
    if (plan.role === currentRole) return 'Current Plan';
    return 'Select Plan';
  };
  
  return (
    <Box>
      {/* <Box sx={{ mb: 6 }}>
        <Typography variant="h4" gutterBottom>
          Choose Your Plan
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Select the plan that best fits your investment needs and goals
        </Typography>
      </Box> */}

      <Grid container spacing={3} justifyContent="center" alignItems="stretch">
        {plans.map((plan) => (
          <Grid 
            item 
            key={plan.title} 
            xs={12} 
            sm={6} 
            md={3} 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column',
            }}
          >
            {/* Banner container with fixed height */}
            <Box sx={{ height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
              {plan.highlighted && (
                <Box
                  sx={{
                    bgcolor: plan.color,
                    color: '#fff',
                    py: 0.5,
                    px: 2,
                    borderRadius: 5,
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    letterSpacing: 1,
                    boxShadow: `0 4px 12px ${alpha(plan.color, 0.3)}`,
                    zIndex: 1,
                    textAlign: 'center',
                    minWidth: 'max-content'
                  }}
                >
                  Most Popular
                </Box>
              )}
              {plan.comingSoon && (
                <Box
                  sx={{
                    bgcolor: alpha(theme.palette.text.secondary, 0.7),
                    color: '#fff',
                    py: 0.5,
                    px: 2,
                    borderRadius: 5,
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    letterSpacing: 1,
                    zIndex: 1,
                    textAlign: 'center',
                    minWidth: 'max-content'
                  }}
                >
                  Coming Soon
                </Box>
              )}
            </Box>
            <Card
              elevation={1}
              sx={{
                width: '100%',
                height: 780, // Increased from 550px to 650px to fit all content
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                borderRadius: 2,
                overflow: 'visible', // Changed from 'hidden' to 'visible'
                border: currentRole === plan.role 
                  ? `2px solid ${theme.palette.primary.main}`
                  : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                ...(currentRole === plan.role && {
                  boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`,
                }),
                ...(plan.disabled && {
                  opacity: 0.7,
                  filter: 'grayscale(20%)'
                }),
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: currentRole === plan.role
                    ? `0 12px 48px ${alpha(theme.palette.primary.main, 0.25)}`
                    : `0 12px 32px ${alpha(theme.palette.common.black, 0.1)}`,
                },
              }}
            >
              <CardHeader
                title={
                  <Typography 
                    variant="h5" 
                    fontWeight="bold" 
                    align="center"
                    sx={{ color: plan.highlighted ? plan.color : 'text.primary', mt: 1 }}
                  >
                    {plan.title}
                  </Typography>
                }
                subheader={
                  <Box sx={{ textAlign: 'center', mt: 1 }}>
                    {(plan.title === 'Premium' || plan.title === 'Premium Plus') && plan.originalPrice ? (
                      <>
                        <Box sx={{ mb: 0.5 }}>
                          <Typography 
                            variant="h6" 
                            component="span" 
                            sx={{ 
                              textDecoration: 'line-through',
                              color: alpha(theme.palette.text.secondary, 0.7),
                              fontWeight: 'medium',
                              mr: 1
                            }}
                          >
                            {plan.originalPrice}
                          </Typography>
                          <Chip
                            label={plan.title === 'Premium' ? "60% OFF" : "50% OFF"}
                            size="small"
                            sx={{
                              bgcolor: alpha(plan.color, 0.15),
                              color: plan.color,
                              fontWeight: 'bold',
                              fontSize: '0.7rem',
                              height: 20
                            }}
                          />
                        </Box>
                        <Typography 
                          variant="h4" 
                          component="span" 
                          fontWeight="bold"
                          sx={{ 
                            color: plan.highlighted ? plan.color : 'text.primary'
                          }}
                        >
                          {plan.price}
                        </Typography>
                        <Typography 
                          variant="subtitle1" 
                          component="span" 
                          sx={{ ml: 1, color: 'text.secondary' }}
                        >
                          /{plan.period}
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Typography 
                          variant="h4" 
                          component="span" 
                          fontWeight="bold"
                          sx={{ color: plan.highlighted ? plan.color : 'text.primary' }}
                        >
                          {plan.price}
                        </Typography>
                        <Typography 
                          variant="subtitle1" 
                          component="span" 
                          sx={{ ml: 1, color: 'text.secondary' }}
                        >
                          /{plan.period}
                        </Typography>
                      </>
                    )}
                  </Box>
                }
                sx={{
                  pb: 1,
                  pt: 3,
                  height: 140, // Fixed height for header section
                  display: 'flex',
                  flexDirection: 'column',
                  '& .MuiCardHeader-content': {
                    display: 'block',
                  },
                }}
              />
              <Divider sx={{ mx: 2, opacity: 0.6 }} />
              <CardContent 
                sx={{ 
                  flexGrow: 1,
                  pt: 2,
                  height: 380, // Increased from 280px to 380px
                  overflow: 'visible', // Changed from 'auto' to 'visible' to remove scrolling
                }}
              >
                <Stack spacing={1.5}>
                  {plan.features.map((feature) => (
                    <Box 
                      key={feature} 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'flex-start',
                        minHeight: 32, // Ensure consistent feature height
                      }}
                    >
                      <CheckIcon 
                        fontSize="small" 
                        sx={{ 
                          mr: 1,
                          mt: 0.3, // Align icon with first line of text
                          color: plan.highlighted ? plan.color : theme.palette.success.main,
                          opacity: plan.highlighted ? 1 : 0.7
                        }} 
                      />
                      <Typography variant="body2">
                        {feature}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
              <CardActions 
                sx={{ 
                  p: 3, 
                  pt: 1, 
                  height: 80, // Keep the same height for action area
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Button
                  fullWidth
                  variant={plan.highlighted ? 'contained' : 'outlined'}
                  color={plan.highlighted ? 'primary' : 'inherit'}
                  onClick={() => onSelect(plan.role)}
                  disabled={plan.role === currentRole || plan.disabled}
                  sx={{ 
                    py: 1.2,
                    borderRadius: 2,
                    ...(plan.highlighted && {
                      bgcolor: plan.color,
                      '&:hover': {
                        bgcolor: alpha(plan.color, 0.85),
                      }
                    }),
                    ...(plan.role === currentRole && {
                      borderColor: plan.color,
                      color: plan.color,
                    })
                  }}
                >
                  {getButtonText(plan)}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default SubscriptionPlans; 